import {
  FieldRefSource,
  TSysAgentStepDefinition,
  TWatchJobStepDefinition,
  TWorkflowDefinition,
  TWorkflowStepDefinition,
  WorkflowStepType,
} from '@commenda/types';

export class ServiceAuditorStepHelper {
  // The agent-platform profile that runs the service auditor; matches the backend profile registry key.
  static readonly SERVICE_AUDITOR_PROFILE_KEY = 'SERVICE_AUDITOR';

  // A plain boolean, not a type predicate: callers already hold an agent step, and a predicate would narrow
  // their else branch to never.
  static isServiceAuditorStep(
    step: Readonly<TWorkflowStepDefinition> | null | undefined
  ): boolean {
    return (
      !!step &&
      ServiceAuditorStepHelper.isAgentStep(step) &&
      step.profileKey === ServiceAuditorStepHelper.SERVICE_AUDITOR_PROFILE_KEY
    );
  }

  // The auditor step a wait step watches, read from the wait step's job id source; null for any other step.
  static findWatchedAuditorStepId(
    definition: Readonly<TWorkflowDefinition> | null | undefined,
    step: Readonly<TWorkflowStepDefinition> | null | undefined
  ): string | null {
    if (!step || !ServiceAuditorStepHelper.isWatchJobStep(step)) return null;
    const watchedStepId = ServiceAuditorStepHelper.getWatchedStepId(step);
    const watchedStep = definition?.steps.find(
      (candidate) => candidate.id === watchedStepId
    );
    return ServiceAuditorStepHelper.isServiceAuditorStep(watchedStep)
      ? watchedStepId
      : null;
  }

  // The wait step that watches an auditor step, found by the same job id source; null when there is none.
  static findWatchStepId(
    definition: Readonly<TWorkflowDefinition> | null | undefined,
    auditorStepId: string
  ): string | null {
    const watchStep = definition?.steps.find(
      (step) =>
        ServiceAuditorStepHelper.isWatchJobStep(step) &&
        ServiceAuditorStepHelper.getWatchedStepId(step) === auditorStepId
    );
    return watchStep?.id ?? null;
  }

  // The auditor step behind a step: itself for an auditor step, the watched auditor for its wait step.
  static findAuditorStepIdForStep(
    definition: Readonly<TWorkflowDefinition> | null | undefined,
    stepId: string | null | undefined
  ): string | null {
    const step = definition?.steps.find((candidate) => candidate.id === stepId);
    if (!step) return null;
    if (ServiceAuditorStepHelper.isServiceAuditorStep(step)) return step.id;
    return ServiceAuditorStepHelper.findWatchedAuditorStepId(definition, step);
  }

  private static getWatchedStepId(
    step: Readonly<TWatchJobStepDefinition>
  ): string | null {
    const { jobIdProvider } = step;
    if (
      jobIdProvider.type !== 'field' ||
      jobIdProvider.ref.source !== FieldRefSource.STEP
    ) {
      return null;
    }
    return jobIdProvider.ref.stepId;
  }

  private static isWatchJobStep(
    step: Readonly<TWorkflowStepDefinition>
  ): step is TWatchJobStepDefinition {
    return step.type === WorkflowStepType.SYS_WATCH_JOB;
  }

  private static isAgentStep(
    step: Readonly<TWorkflowStepDefinition>
  ): step is TSysAgentStepDefinition {
    return step.type === WorkflowStepType.SYS_AI_AGENT;
  }
}
